import React, { useCallback, useEffect, useRef, useState } from 'react';

const YT_ORIGIN = 'https://www.youtube-nocookie.com';
const YT_ALLOWED_ORIGINS = [YT_ORIGIN, 'https://www.youtube.com'];

// YT.PlayerState
const STATE_UNSTARTED = -1;
const STATE_ENDED = 0;
const STATE_PLAYING = 1;
const STATE_PAUSED = 2;

// Toleranz beim Vergleich von Sekundenwerten (Player meldet nicht exakt).
const SEEK_TOLERANCE = 3;
// Nach so langer Zeit wird ein nicht angekommener Seek nicht weiter erzwungen.
const RESTORE_TIMEOUT_MS = 15_000;

function extractYoutubeId(url: string): string | null {
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

interface Props {
  url: string;
  title: string;
  autoplay?: boolean;
  startSeconds?: number;
  onProgress?: (seconds: number, duration: number) => void;
  /** Feuert, sobald der Zuschauer pausiert — mit der exakten Pausenposition. */
  onPause?: (seconds: number, duration: number) => void;
}

const YouTubeEmbed: React.FC<Props> = ({
  url,
  title,
  autoplay = false,
  startSeconds = 0,
  onProgress,
  onPause,
}) => {
  const ytId = extractYoutubeId(url);

  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;
  const pauseRef = useRef(onPause);
  pauseRef.current = onPause;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Die Startposition wird beim Mounten eingefroren. Ein sich änderndes `src`
  // lädt den Player neu und zieht den Zuschauer dabei auf einen alten
  // Zeitstempel zurück — genau das darf nie durch ein Re-Render passieren.
  const [initialStart] = useState(() =>
    Number.isFinite(startSeconds) ? Math.max(0, Math.floor(startSeconds)) : 0
  );

  const lastTimeRef = useRef(initialStart);
  const lastDurationRef = useRef(0);
  const hasProgressRef = useRef(false);
  const isPlayingRef = useRef(autoplay);
  const loadCountRef = useRef(0);
  const readyCountRef = useRef(0);
  // Position, auf die zurückgesprungen werden muss, sobald sich der Player meldet.
  const pendingRestoreRef = useRef<number | null>(null);
  // Läuft ein Seek: Meldungen von davor werden verworfen, damit die veraltete
  // Position nicht gespeichert wird.
  const restoreRef = useRef<{ target: number; expiresAt: number } | null>(null);

  const postToPlayer = useCallback((message: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ id: 1, channel: 'widget', ...message }),
      YT_ORIGIN
    );
  }, []);

  const postCommand = useCallback(
    (func: string, args: unknown[] = []) => postToPlayer({ event: 'command', func, args }),
    [postToPlayer]
  );

  // Holt den Player an die Stelle zurück, an der der Zuschauer war. Nötig, wenn
  // YouTube (oder der Browser) das Embed neu initialisiert: der Neustart spielt
  // den `start`-Parameter aus der URL erneut ab, wodurch ein pausiertes Video
  // scheinbar von selbst zurückspringt.
  const beginRestore = useCallback(
    (target: number) => {
      pendingRestoreRef.current = null;
      restoreRef.current = { target, expiresAt: Date.now() + RESTORE_TIMEOUT_MS };
      postCommand('seekTo', [target, true]);
      // Ein Neustart läuft wegen autoplay von selbst los — wer pausiert hatte,
      // soll aber pausiert bleiben.
      if (!isPlayingRef.current) postCommand('pauseVideo');
    },
    [postCommand]
  );

  const requestRestore = useCallback(() => {
    if (!hasProgressRef.current) return;
    if (restoreRef.current) return;
    if (lastTimeRef.current <= 1) return;
    pendingRestoreRef.current = lastTimeRef.current;
  }, []);

  // Listen for YouTube postMessage events (requires enablejsapi=1 in the URL)
  useEffect(() => {
    if (!ytId) return;

    const handleTime = (seconds: number, duration: number) => {
      if (duration > 0) lastDurationRef.current = duration;

      const pending = pendingRestoreRef.current;
      if (pending !== null) {
        pendingRestoreRef.current = null;
        if (Math.abs(seconds - pending) > SEEK_TOLERANCE) {
          beginRestore(pending);
          return;
        }
      }

      const restore = restoreRef.current;
      if (restore) {
        if (Date.now() > restore.expiresAt) {
          // Seek ist nicht angekommen — dem Player wieder vertrauen.
          restoreRef.current = null;
        } else if (Math.abs(seconds - restore.target) > SEEK_TOLERANCE) {
          return;
        } else {
          restoreRef.current = null;
        }
      }

      lastTimeRef.current = seconds;
      hasProgressRef.current = true;
      progressRef.current?.(seconds, duration);
    };

    const handleState = (state: number) => {
      if (state === STATE_PLAYING) isPlayingRef.current = true;
      else if (state === STATE_PAUSED || state === STATE_ENDED) isPlayingRef.current = false;

      if (state === STATE_PAUSED) {
        pauseRef.current?.(lastTimeRef.current, lastDurationRef.current);
      }

      // Zurück auf "unstarted", obwohl schon abgespielt wurde: der Player hat
      // sich neu aufgebaut und steht wieder am alten Startpunkt.
      if (state === STATE_UNSTARTED) requestRestore();
    };

    const handler = (event: MessageEvent) => {
      // Only trust messages that actually come from the YouTube frame.
      if (!YT_ALLOWED_ORIGINS.includes(event.origin)) return;
      if (typeof event.data !== 'string') return;

      let data: { event?: unknown; info?: unknown };
      try {
        data = JSON.parse(event.data);
      } catch {
        return; // not a YouTube message, ignore
      }
      if (!data || typeof data !== 'object') return;

      if (data.event === 'onReady') {
        readyCountRef.current += 1;
        // Ein zweites onReady heißt: YouTube hat den Player neu gebaut.
        if (readyCountRef.current > 1) requestRestore();
        const pending = pendingRestoreRef.current;
        if (pending !== null) beginRestore(pending);
        return;
      }

      const info = data.info as
        | { currentTime?: unknown; duration?: unknown; playerState?: unknown }
        | undefined;

      if (
        (data.event === 'infoDelivery' || data.event === 'initialDelivery') &&
        info &&
        typeof info.currentTime === 'number' &&
        typeof info.duration === 'number' &&
        info.duration > 0
      ) {
        handleTime(Math.floor(info.currentTime), Math.floor(info.duration));
      }

      if (data.event === 'onStateChange' && typeof data.info === 'number') {
        handleState(data.info);
      } else if (info && typeof info.playerState === 'number') {
        handleState(info.playerState);
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [ytId, beginRestore, requestRestore]);

  // Jedes erneute Laden des iframes (Reload durch YouTube oder den Browser)
  // startet wieder beim eingefrorenen `start` — Position merken und danach
  // wiederherstellen.
  const handleIframeLoad = useCallback(() => {
    loadCountRef.current += 1;
    // Handshake der IFrame-API, damit der Player weiterhin Fortschritt meldet.
    postToPlayer({ event: 'listening' });
    if (loadCountRef.current > 1) requestRestore();
  }, [postToPlayer, requestRestore]);

  if (!ytId) {
    return (
      <div className="aspect-video bg-black w-full overflow-hidden flex items-center justify-center">
        <p className="text-white/60 text-sm">Video konnte nicht geladen werden.</p>
      </div>
    );
  }

  const params = new URLSearchParams({ rel: '0', playsinline: '1', enablejsapi: '1' });
  if (autoplay) params.set('autoplay', '1');
  if (initialStart > 0) params.set('start', String(initialStart));

  return (
    <iframe
      ref={iframeRef}
      onLoad={handleIframeLoad}
      // Erweiterter Datenschutzmodus: keine Cookies vor dem Abspielen
      src={`${YT_ORIGIN}/embed/${ytId}?${params.toString()}`}
      title={title}
      className="aspect-video w-full block"
      style={{ border: 0 }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
      allowFullScreen={true}
    />
  );
};

export default YouTubeEmbed;
